import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  fetchEconomicCalendar,
  fetchMarketNews,
  getMarketContentLanguage,
  marketDataConfig,
  type EconomicCalendarItem,
  type MarketNewsItem,
} from '../../../services/marketDataApi';

const localDateKey = (date: Date) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: marketDataConfig.timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
};

const addDays = (dateKey: string, days: number) => {
  const [yearText = '1970', monthText = '01', dayText = '01'] = dateKey.split('-');
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const date = new Date(Date.UTC(year, month - 1, day + days, 12));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(
    date.getUTCDate()
  ).padStart(2, '0')}`;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return { date: '—', time: '—' };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: '—', time: '—' };
  return {
    date: new Intl.DateTimeFormat('en-GB', {
      timeZone: marketDataConfig.timeZone,
      day: '2-digit',
      month: '2-digit',
    }).format(date),
    time: new Intl.DateTimeFormat('en-GB', {
      timeZone: marketDataConfig.timeZone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date),
  };
};

const formatValue = (value: unknown, unit?: string | null) => {
  if (value === null || value === undefined || value === '') return '—';
  const numeric = Number(value);
  const formatted = Number.isFinite(numeric)
    ? new Intl.NumberFormat('en-GB', { maximumFractionDigits: 4 }).format(numeric)
    : String(value);
  const normalizedUnit = String(unit || '').trim();
  if (!normalizedUnit) return formatted;
  if (['%', 'bps', '°'].includes(normalizedUnit)) return `${formatted}${normalizedUnit}`;
  return `${formatted} ${normalizedUnit}`;
};

const countryFlag = (countryCode?: string | null) => {
  const code = String(countryCode || '').toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return '●';
  return String.fromCodePoint(...[...code].map((character) => 127397 + character.charCodeAt(0)));
};

const importanceClass = (impact?: string | null) => {
  const normalized = String(impact || '').toUpperCase();
  if (normalized === 'HIGH') return 'bg-red-500';
  if (normalized === 'MEDIUM') return 'bg-orange-500';
  return 'bg-yellow-500';
};

const sortCalendar = (items: EconomicCalendarItem[]) =>
  [...items].sort(
    (left, right) => new Date(left.eventTimeUtc).getTime() - new Date(right.eventTimeUtc).getTime()
  );

const sortNews = (items: MarketNewsItem[]) =>
  [...items].sort(
    (left, right) =>
      new Date(right.publishedAtUtc).getTime() - new Date(left.publishedAtUtc).getTime()
  );

export default function CalendarSection() {
  const { t, i18n } = useTranslation();
  const apiLanguage = getMarketContentLanguage(i18n.resolvedLanguage || i18n.language);
  const [calendar, setCalendar] = useState<EconomicCalendarItem[]>([]);
  const [news, setNews] = useState<MarketNewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const dateRange = useMemo(() => {
    const from = localDateKey(new Date());
    return { from, to: addDays(from, 7) };
  }, []);

  useEffect(() => {
    let activeController: AbortController | null = null;
    setCalendar([]);
    setNews([]);

    const loadContent = async (showLoading: boolean) => {
      activeController?.abort();
      activeController = new AbortController();
      if (showLoading) setLoading(true);

      try {
        const [calendarResponse, newsResponse] = await Promise.all([
          fetchEconomicCalendar(dateRange.from, dateRange.to, activeController.signal, {
            language: apiLanguage,
          }),
          fetchMarketNews(
            { type: 'ALL', page: 0, size: 20, language: apiLanguage },
            activeController.signal,
          ),
        ]);
        setCalendar(sortCalendar(calendarResponse.items).slice(0, 12));
        setNews(sortNews(newsResponse.items).slice(0, 12));
        setLastUpdate(new Date());
        setError('');
      } catch (loadError) {
        if (!(loadError instanceof DOMException && loadError.name === 'AbortError')) {
          setError(loadError instanceof Error ? loadError.message : 'Unable to load market content.');
        }
      } finally {
        setLoading(false);
      }
    };

    void loadContent(true);
    const intervalId = window.setInterval(
      () => void loadContent(false),
      marketDataConfig.contentRefreshMs
    );

    return () => {
      activeController?.abort();
      window.clearInterval(intervalId);
    };
  }, [apiLanguage, dateRange.from, dateRange.to]);

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('home.mktInt')}</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            {t('home.mktInt.desc')}
          </p>
          <div className="text-sm text-gray-500 mt-2">
            {t('home.lastUpdate')}:{' '}
            {lastUpdate
              ? new Intl.DateTimeFormat('en-GB', {
                  timeZone: marketDataConfig.timeZone,
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                }).format(lastUpdate)
              : '—'}{' '}
            ({t('markets.hongKongTime', { defaultValue: 'Hong Kong time' })})
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
            <div className="flex items-start gap-3">
              <i className="ri-error-warning-line text-lg" />
              <span>{error}</span>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
            <div className="p-6 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 flex items-center">
                    <i className="ri-calendar-line text-orange-500 mr-2" />
                    {t('home.ecoCal')}
                  </h3>
                  <p className="mt-1 text-xs text-gray-500">
                    {dateRange.from} {t('calendar.to', { defaultValue: 'to' })} {dateRange.to} ·{' '}
                    {t('calendar.hongKongTime', { defaultValue: 'Hong Kong time (GMT+8)' })}
                  </p>
                </div>
                <Link
                  to="/grand-opening?tab=calendar"
                  className="text-orange-500 hover:text-orange-600 text-sm font-medium flex items-center gap-1 whitespace-nowrap"
                >
                  {t('home.seeMore')}
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>

            <div className="divide-y divide-gray-100 max-h-[520px] overflow-y-auto">
              {loading && calendar.length === 0 &&
                Array.from({ length: 6 }, (_, index) => (
                  <div key={index} className="p-4 animate-pulse">
                    <div className="h-4 bg-gray-100 rounded w-3/4 mb-3" />
                    <div className="h-3 bg-gray-100 rounded w-1/2" />
                  </div>
                ))}

              {!loading && calendar.length === 0 && (
                <div className="p-10 text-center text-gray-500">
                  <i className="ri-calendar-event-line text-4xl text-gray-300 block mb-3" />
                  {t('calendar.noEventsForPeriod', {
                    defaultValue: 'No economic events are available for this period.',
                  })}
                </div>
              )}

              {calendar.map((event) => {
                const dateTime = formatDateTime(event.eventTime);
                const released = event.actual !== null && event.actual !== undefined;

                return (
                  <div key={event.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="w-14 flex-shrink-0 text-center">
                        <div className="text-lg leading-none">{countryFlag(event.countryCode)}</div>
                        <div className="mt-1 text-xs font-semibold text-gray-700">
                          {event.currency || event.countryCode || '—'}
                        </div>
                      </div>

                      <div className="w-16 flex-shrink-0 text-xs text-gray-500 leading-5">
                        <div>{dateTime.date}</div>
                        <div className="font-medium text-gray-700">{dateTime.time}</div>
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="text-sm font-semibold text-gray-900">{event.event}</span>
                          <span
                            title={`${event.impact || 'Low'} impact`}
                            className={`w-2.5 h-2.5 rounded-full ${importanceClass(event.impact)}`}
                          />
                          <span
                            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold border ${
                              released
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                                : 'bg-blue-50 text-blue-700 border-blue-100'
                            }`}
                          >
                            {released ? t('calendar.released') : t('calendar.pending')}
                          </span>
                        </div>

                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div>
                            <span className="block text-gray-400">{t('home.previous')}</span>
                            <span className="font-medium text-gray-700">
                              {formatValue(event.previous, event.unit)}
                            </span>
                          </div>
                          <div>
                            <span className="block text-gray-400">{t('home.forecast')}</span>
                            <span className="font-medium text-gray-700">
                              {formatValue(event.estimate, event.unit)}
                            </span>
                          </div>
                          <div>
                            <span className="block text-gray-400">{t('home.actual')}</span>
                            <span className={released ? 'font-bold text-gray-900' : 'font-medium text-blue-600'}>
                              {formatValue(event.actual, event.unit)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 flex items-center">
                    <i className="ri-news-line text-orange-500 mr-2" />
                    {t('home.mktNews')}
                  </h3>
                  <p className="mt-1 text-xs text-gray-500">{t('news.subtitle')}</p>
                </div>
                <Link
                  to="/grand-opening?tab=news"
                  className="text-orange-500 hover:text-orange-600 text-sm font-medium flex items-center gap-1 whitespace-nowrap"
                >
                  {t('home.seeMore')}
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>

            <div className="divide-y divide-gray-100 max-h-[520px] overflow-y-auto">
              {loading && news.length === 0 &&
                Array.from({ length: 6 }, (_, index) => (
                  <div key={index} className="p-4 animate-pulse">
                    <div className="h-3 bg-gray-100 rounded w-1/3 mb-3" />
                    <div className="h-4 bg-gray-100 rounded w-full mb-2" />
                    <div className="h-4 bg-gray-100 rounded w-4/5" />
                  </div>
                ))}

              {!loading && news.length === 0 && (
                <div className="p-10 text-center text-gray-500">
                  <i className="ri-newspaper-line text-4xl text-gray-300 block mb-3" />
                  {t('news.noCurrentNews', { defaultValue: 'No market news is available right now.' })}
                </div>
              )}

              {news.map((article) => {
                const dateTime = formatDateTime(article.publishedAt);

                return (
                  <div key={article.id} className="group p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 pt-1.5">
                        <div className="w-2 h-2 bg-orange-500 rounded-full" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mb-2">
                          <span>{dateTime.date}</span>
                          <span>{dateTime.time}</span>
                        </div>
                        <p className="text-sm font-medium text-gray-800 leading-relaxed">
                          {article.title}
                        </p>
                        {article.snippet && (
                          <p className="mt-2 text-xs text-gray-500 leading-5 line-clamp-2">{article.snippet}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="text-center mt-8 text-xs text-gray-500">
          <p>
            {t('markets.informationDisclaimer', {
              defaultValue:
                'Prices and market information are indicative and may be delayed. Content is for information only and does not constitute investment advice.',
            })}
          </p>
        </div>
      </div>
    </section>
  );
}
